import { useState, useRef, ReactNode, Fragment, useCallback } from 'react';

import { Box, Stack } from '@mui/material';
import clsx from 'clsx';
import { useCombobox } from 'downshift';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import type { MultiAutocompleteProps } from './types';
import { useSortFreeze } from './useSortFreeze';
import * as Icons from '../../../icons';
import Scroller from '../../../scroller/Scroller';
import { VirtuosoScroller } from '../../../scroller/VirtuosoScroller';
import { dropdownClassNames } from '../classNames';
import { NoItemsFound } from '../components/NoItemsFound';
import { useCustomItemAddition } from '../hooks/useCustomItemAddition';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemStringConverters } from '../hooks/useItemStringConverters';
import { useKeyboardNavigationScrolling } from '../hooks/useKeyboardNavigationScrolling';
import {
  MainContainer,
  Label,
  ToggleButton,
  CaretContainer,
  HintMessage,
  PrefixContainer,
  PlaceholderContainer,
  SearchInputContainer,
  SearchInput,
  MenuList,
  CustomItemHint,
  DropdownContainer,
  StyledPopper as Popper,
  PrefixOutside,
  SuffixOutside,
  SelectedItemsContainer,
  autocompleteSizeToFormLabelSize,
} from '../SharedStyledComponents';
import { ChipDisplay } from './components/ChipDisplay';
import { useItemSelection } from './hooks/useItemSelection';
import { EnhancedItem, useEnhancedItems } from '../hooks/useEnhancedItems';
import { renderMenuItemByType } from '../utils/renderMenuItems';

/**
 * MultiAutocomplete component for multiple item selection.
 * Allows users to select multiple items from a dropdown list with search functionality.
 */
export const MultiAutocomplete = <Item,>({
  label,
  placeholder = 'Select an item...',
  searchInputPlaceholder = 'Search...',
  prefix,
  prefixOutside,
  suffixOutside,
  hint,
  error,
  success = false,
  items: propItems,
  filterItems: customFilterItems,
  renderMenuItem: customRenderMenuItem,
  renderSelectedItems: customRenderSelectedItems,
  renderMenuItemGroupLabel: customRenderMenuItemGroupLabel,
  value = [],
  isItemDisabled: customIsItemDisabled,
  isGroupDisabled,
  disabled = false,
  readOnly = false,
  renderNoItemsFound: customRenderNoItemsFound,
  addCustomItem: addCustomItemProp,
  size = 'medium',
  testId = 'multi-autocomplete-root',
  showItemCheckbox = true,
  showSelectAllItem = false,
  addCustomItemHint = 'Tap enter key to add custom item!',
  getLabel,
  getStringValue,
  className,
  showSelectedOptionsFirst: showSelectedOptionsFirstProp = false,
  displayChips = false,
  chipSx,
  minShownChips = 4,
  // events
  onOpen,
  onChange,
  onToggleBlur,
  onToggleFocus,
  onSearchInputBlur,
  onSearchInputFocus,
  onToggleMouseOver,
  onToggleMouseLeave,
  popoverSize,
  useVirtualization = false,
  groupBy,
}: MultiAutocompleteProps<Item>) => {
  // sorting selected items to the top of menu is not supported with grouping
  const showSelectedOptionsFirst = groupBy
    ? false
    : showSelectedOptionsFirstProp;

  // adding custom items is not supported with grouping
  const addCustomItem = groupBy ? undefined : addCustomItemProp;

  const anchorRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement | VirtuosoHandle | null>(null);
  const popoverMaxHeight: number | string = popoverSize?.maxHeight || 250;
  const [virtuosoHeight, setVirtuosoHeight] = useState(popoverMaxHeight);

  const [searchInputValue, setSearchInputValue] = useState('');

  const { itemToStringLabel, itemToStringValue } = useItemStringConverters({
    getLabel,
    getStringValue,
  });

  // Create a ref to store triggerSort so we can use it in callbacks defined before the hook
  const triggerSortRef = useRef<(() => void) | null>(null);

  const handleCustomItemAdded = useCallback(
    (customItem: Item) => {
      const customItemValue = itemToStringValue(customItem);
      const newValues = [...value, customItemValue];
      const selectedItems: Item[] = propItems.filter((item) =>
        newValues.includes(itemToStringValue(item))
      );
      onChange?.({
        values: newValues,
        selectedItems: [...selectedItems, customItem],
      });
      setSearchInputValue('');

      // Trigger re-sort when custom item is added
      triggerSortRef.current?.();
    },
    [value, propItems, itemToStringValue, onChange]
  );

  const { items, isItemUniqueValue, handleAddCustomItem } =
    useCustomItemAddition({
      searchInputValue,
      addCustomItem,
      propItems,
      itemToStringValue,
      onItemAdded: handleCustomItemAdded,
    });

  const filteredItems = useFilteredItems({
    items,
    searchInputValue,
    itemToStringLabel,
    customFilterItems,
  });

  const enhancedFilteredItems = useEnhancedItems({
    items: filteredItems,
    value,
    showSelectAllItem,
    groupBy,
    isGroupDisabled,
    itemToStringValue,
    itemToStringLabel,
    isItemDisabled: customIsItemDisabled,
  });

  const { sortedItems: enhancedFilteredItemsSorted, triggerSort } =
    useSortFreeze({
      items: enhancedFilteredItems,
      enabled: showSelectedOptionsFirst,
      selectedCount: value.length,
    });

  // Update the ref with the latest triggerSort function
  triggerSortRef.current = triggerSort;

  const {
    handleItemSelection,
    removeFromSelection,
    removeLastItemFromSelection,
    selectedItems,
  } = useItemSelection({
    value,
    items,
    enhancedFilteredItems,
    itemToStringValue,
    onChange,
  });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    highlightedIndex,
  } = useCombobox<EnhancedItem<Item>>({
    selectedItem: null, // there's no actual selection happening in the hook, we're doing it custom via selectedItems.
    inputValue: searchInputValue, // controlled
    items: enhancedFilteredItemsSorted,
    onInputValueChange({ inputValue }) {
      setSearchInputValue(inputValue);
    },

    onIsOpenChange({ isOpen }) {
      if (isOpen) {
        // each time the popper is opened, we want to reset the search input
        setSearchInputValue('');
        onOpen?.();

        // Trigger re-sort when menu opens
        triggerSort();
      } else {
        // because we are using the combobox, we need to manually call the onBlur event
        // onBlur does not fire automatically when component no longer exists.
        // therefore, we do this manually. You might say we simulate the blur.
        onSearchInputBlur?.();

        // when the popper is closed, we want to focus on the toggle button
        toggleButtonRef.current?.focus();
      }
    },

    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem === null) {
        return;
      }
      handleItemSelection(selectedItem);
    },

    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        // fixes behaviour bug, after selection, the first character you type in to search field was ignored.
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: state.inputValue,
          };
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...actionAndChanges.changes,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
            inputValue: state.inputValue, // keep the state value
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '', // don't add the item string as input value at selection.
          };
        default:
          return changes;
      }
    },

    itemToString: (item) => item?.value ?? '',
    isItemDisabled: (item) => item.isDisabled,
  });

  // Handle keyboard navigation scrolling for highlighted items
  const { handleItemMouseMove } = useKeyboardNavigationScrolling(
    isOpen,
    highlightedIndex,
    {
      isVirtualized: useVirtualization,
      menuRef,
    }
  );

  // Wrap getItemProps to inject mouse move handler
  const getItemPropsWithMouseHandler = useCallback(
    (options: Parameters<typeof getItemProps>[0]) => {
      const itemProps = getItemProps(options);
      return {
        ...itemProps,
        onMouseMove: (e: React.MouseEvent) => {
          handleItemMouseMove();
          itemProps.onMouseMove?.(e);
        },
      };
    },
    [getItemProps, handleItemMouseMove]
  );

  // due to how we restructure this combobox, aria-labelledby is not needed.
  const { 'aria-labelledby': _, ...searchInputProps } = getInputProps(
    {
      onKeyDown: (e) => {
        if (e.key === 'Escape' && isOpen) {
          e.stopPropagation();
          e.currentTarget.blur();
        }

        if (e.key === 'Enter') {
          handleAddCustomItem();
        }
      },
    },
    { suppressRefError: true }
  );

  // in order to customise behavior of the toggle button, we need to hijack the ref and tabIndex
  const {
    ref: _toggleButtonRef,
    tabIndex: _toggleButtonTabIndex,
    ...toggleButtonProps
  } = getToggleButtonProps({
    onKeyDown: (e) => {
      if (e.key === 'Backspace') {
        removeLastItemFromSelection();
      }
    },
    onMouseLeave: onToggleMouseLeave,
    onMouseOver: onToggleMouseOver,
    onFocus: onToggleFocus,
    onBlur: onToggleBlur,
  });

  const renderSelectedItems = (selectedItems: Item[]): ReactNode => {
    if (customRenderSelectedItems) {
      return customRenderSelectedItems({
        value,
        selectedItems,
      });
    }

    const renderSeparator = (index: number, totalItems: number): ReactNode =>
      index < totalItems - 1 ? ',\u00A0' : null;

    return (
      <Stack direction="row" flexWrap="wrap">
        {selectedItems.map((item, index, array) => (
          <span data-testid={`selected-item-${index}`} key={`index-${index}`}>
            {itemToStringLabel(item)}
            {renderSeparator(index, array.length)}
          </span>
        ))}
      </Stack>
    );
  };

  const attributeClassNames = clsx({
    [dropdownClassNames.open]: isOpen,
    [dropdownClassNames.disabled]: disabled,
    [dropdownClassNames.readOnly]: readOnly,
    [dropdownClassNames.error]: error,
    [dropdownClassNames.success]: success,
    [dropdownClassNames.sizeSmall]: size === 'small',
    [dropdownClassNames.sizeMedium]: size === 'medium',
    [dropdownClassNames.sizeLarge]: size === 'large',
  });

  return (
    <Box
      data-testid={testId}
      className={clsx(
        dropdownClassNames.root,
        dropdownClassNames.multiAutoComplete,
        className,
        attributeClassNames
      )}
    >
      <input
        className={dropdownClassNames.hiddenInput}
        type="hidden"
        value={value.length > 0 ? JSON.stringify(value) : ''}
        data-testid="hidden-value-input"
        readOnly={readOnly}
        disabled={disabled}
      />
      <MainContainer ref={anchorRef}>
        {prefixOutside && (
          <PrefixOutside data-testid="prefix-outside" inputHasLabel={!!label}>
            {prefixOutside}
          </PrefixOutside>
        )}

        {label && (
          <Label
            {...getLabelProps()}
            className="downshift-label"
            testId="label"
            size={autocompleteSizeToFormLabelSize(size)}
            hasOutsidePrefix={!!prefixOutside}
            hasOutsideSuffix={!!suffixOutside}
            disabled={disabled}
          >
            {label}
          </Label>
        )}

        <ToggleButton
          type="button" // to prevent accidental form submissions
          data-testid="toggle-button"
          disabled={disabled || readOnly}
          aria-disabled={disabled || readOnly}
          aria-invalid={!!error}
          ref={toggleButtonRef}
          tabIndex={0}
          isOpen={isOpen}
          error={!!error}
          success={success}
          size={size}
          readOnly={readOnly}
          hasOutsidePrefix={!!prefixOutside}
          hasOutsideSuffix={!!suffixOutside}
          {...toggleButtonProps}
        >
          {prefix ? (
            <PrefixContainer data-testid="prefix" size={size}>
              {prefix}
            </PrefixContainer>
          ) : null}

          {selectedItems.length ? (
            <SelectedItemsContainer data-testid="selected-items" size={size}>
              {renderSelectedItems(selectedItems)}
            </SelectedItemsContainer>
          ) : (
            <PlaceholderContainer
              data-testid="placeholder"
              size={size}
              disabled={disabled}
            >
              {placeholder}
            </PlaceholderContainer>
          )}

          <CaretContainer isOpen={isOpen} size={size} disabled={disabled}>
            <Icons.CaretDown size={16} />
          </CaretContainer>
        </ToggleButton>

        {(!!error || !!hint) && (
          <HintMessage
            data-testid="hint-message"
            size={size}
            error={!!error}
            hasOutsidePrefix={!!prefixOutside}
            hasOutsideSuffix={!!suffixOutside}
          >
            {error || hint}
          </HintMessage>
        )}

        {suffixOutside && (
          <SuffixOutside data-testid="suffix-outside" inputHasLabel={!!label}>
            {suffixOutside}
          </SuffixOutside>
        )}

        {displayChips && (
          <ChipDisplay
            selectedItems={selectedItems}
            minShownChips={minShownChips}
            itemToStringLabel={itemToStringLabel}
            onRemoveItem={removeFromSelection}
            prefixOutside={prefixOutside}
            chipSx={chipSx}
            readOnly={readOnly}
          />
        )}
      </MainContainer>

      <Popper
        className={attributeClassNames}
        // due to how poppers are rendered, their testid must be unique, scoping with test drivers is not enough.
        data-testid={`${testId}-popper`}
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ maxWidth: popoverSize?.maxWidth }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'clippingParents',
            },
          },
        ]}
      >
        <DropdownContainer>
          <SearchInputContainer>
            <Icons.MagnifyingGlass size={15} />
            <SearchInput
              data-testid="search-input"
              placeholder={searchInputPlaceholder}
              autoFocus
              onFocus={onSearchInputFocus}
              {...searchInputProps}
            />
          </SearchInputContainer>

          {isItemUniqueValue && (
            <CustomItemHint data-testid="add-custom-item-hint">
              {addCustomItemHint}
            </CustomItemHint>
          )}

          {filteredItems.length === 0 && (
            <NoItemsFound
              customRenderNoItemsFound={customRenderNoItemsFound}
              searchInputValue={searchInputValue}
              items={items}
            />
          )}

          {useVirtualization ? (
            <Virtuoso
              data-testid="option-item-container"
              data={enhancedFilteredItemsSorted}
              totalListHeightChanged={setVirtuosoHeight}
              style={{
                maxHeight: popoverMaxHeight,
                height: virtuosoHeight,
              }}
              itemContent={(index, item) =>
                renderMenuItemByType({
                  item,
                  index,
                  showCheckbox: showItemCheckbox,
                  itemProps: getItemPropsWithMouseHandler({ item, index }),
                  customRenderMenuItem,
                  customRenderMenuItemGroupLabel,
                })
              }
              components={{
                Scroller: VirtuosoScroller,
              }}
              {...getMenuProps(
                { ref: menuRef as any },
                { suppressRefError: true }
              )}
            />
          ) : (
            <Scroller
              sx={{
                maxHeight: popoverMaxHeight,
                overflowY: 'auto',
              }}
            >
              <MenuList
                data-testid="option-item-container"
                {...getMenuProps(
                  { ref: menuRef as any },
                  { suppressRefError: true }
                )}
              >
                {enhancedFilteredItemsSorted.map((item, index) => (
                  <Fragment key={index}>
                    {renderMenuItemByType({
                      item,
                      index,
                      showCheckbox: showItemCheckbox,
                      itemProps: getItemPropsWithMouseHandler({ item, index }),
                      customRenderMenuItem,
                      customRenderMenuItemGroupLabel,
                    })}
                  </Fragment>
                ))}
              </MenuList>
            </Scroller>
          )}
        </DropdownContainer>
      </Popper>
    </Box>
  );
};

MultiAutocomplete.classNames = dropdownClassNames;
